import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Select from 'react-select';
import store2 from 'store2';
import { useTranslate } from '../../mixins/translate/translate';
import { login } from '../../store/authentication/actions';
import { IStoreState } from '../../store/IStoreState';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { reactSelectDefaultStyles } from '../../utils/reactSelectDefaultStyles';
import { appContext } from '../appContext';
import {
    AlertMicro,
    EditPanelButton,
    EditPanelButtons,
    EditPanelInput,
    EditPanelInputPassword,
    EditPanelSection,
    LinkButton,
    LoginHeader,
    PanelBody,
} from '../elements';

interface ILoginProps {
    tempLogin: boolean;
    navigateToForgetPassword: () => void;
}

function LoginApplicationHeader() {
    const currentAppContext = useContext(appContext.context);
    const { applicationTitle } = currentAppContext.globalSettings;

    return <LoginHeader>Login {!isCustomerVersion('ronik') ? applicationTitle : ''}</LoginHeader>;
}

export function Login(props: ILoginProps) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isLoginFailed = useSelector((state: IStoreState) => state.authentication.failed);
    const code = useSelector((state: IStoreState) => state.authentication.code);
    const restrictedHostName = useSelector((state: IStoreState) => state.authentication.restrictedHostName);
    const companies = useSelector((state: IStoreState) => state.authentication.companies);
    const [emailAddress, setEmailAddress] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [otpToken, setOtpToken] = React.useState('');
    const [company, setCompany] = React.useState('');
    const [isShowOtpInput, setShowOtpInput] = React.useState(false);
    const [isShowCompanySelect, setShowCompanySelect] = React.useState(false);

    React.useEffect(() => {
        if (code === 'OTP_NEEDED') {
            setShowOtpInput(true);
            setShowCompanySelect(false);
        } else if (code === 'SELECT_COMPANY') {
            setShowCompanySelect(true);
            setShowOtpInput(false);
        }
    }, [code]);

    // eslint-disable-next-line @typescript-eslint/require-await
    async function loginClicked() {
        store2.clearAll();
        const urlPath = window.location.pathname;
        dispatch(login(emailAddress, password, otpToken, company) as any);
        if (urlPath.startsWith('/surveys/')) {
            navigate(urlPath);
        } else if (urlPath === '/logOut') {
            navigate('/dashboard');
        }
    }

    return (
        <>
            <div className="panel mt30">
                {isLoginFailed &&
                    code &&
                    !['BLOCKED', 'OTP_NEEDED', 'OTP_INVALID', 'WRONG_HOSTNAME', 'SELECT_COMPANY'].includes(code) &&
                    !props.tempLogin && <AlertMicro text={translate('login.failMessage')} severity="warning" />}
                {isLoginFailed && code === 'OTP_INVALID' && <AlertMicro text={translate('login.otpCodeInvalid')} severity="warning" />}
                {isLoginFailed && code === 'BLOCKED' && !props.tempLogin && <AlertMicro text={translate('login.blocked')} severity="warning" />}
                {isLoginFailed && code === 'WRONG_HOSTNAME' && !props.tempLogin && (
                    <AlertMicro severity="warning">
                        {translate('login.invalidHost')} <a href={`https://${restrictedHostName}`}>https://{restrictedHostName}</a>
                    </AlertMicro>
                )}
                {isLoginFailed && props.tempLogin && <AlertMicro text="Invalid signature" severity="warning" />}
                <LoginApplicationHeader />
                <form
                    method="get"
                    id="admin-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                    }}
                >
                    <PanelBody>
                        {!isShowOtpInput && !isShowCompanySelect && (
                            <>
                                <EditPanelSection>
                                    <label htmlFor="useremail" className="field-label text-muted fs18 mb10">
                                        {translate('common.emailAddress')}
                                    </label>
                                    <EditPanelInput
                                        name="userEmail"
                                        autoComplete="username"
                                        placeholder={translate('login.enterEmailAddress')}
                                        value={emailAddress}
                                        icon="fa fa-user"
                                        handleChange={(value) => setEmailAddress(value)}
                                        handleEnter={loginClicked}
                                        autoFocus
                                    />
                                </EditPanelSection>

                                <EditPanelSection>
                                    <label htmlFor="password" className="field-label text-muted fs18 mb10">
                                        {translate('common.password')}
                                    </label>
                                    <EditPanelInputPassword
                                        name="password"
                                        autoComplete="current-password"
                                        placeholder={translate('login.enterPassword')}
                                        value={password}
                                        handleChange={(value) => setPassword(value)}
                                        handleEnter={loginClicked}
                                    />
                                </EditPanelSection>
                            </>
                        )}
                        {isShowOtpInput && !isShowCompanySelect && (
                            <EditPanelSection>
                                <label htmlFor="otpToken" className="field-label text-muted fs18 mb10">
                                    {translate('login.enterOtpCode')}
                                </label>
                                <EditPanelInput
                                    name="otpToken"
                                    autoComplete="one-time-code"
                                    placeholder={translate('login.otpCode')}
                                    value={otpToken}
                                    autoFocus
                                    maxlength={6}
                                    handleChange={(value) => setOtpToken(value)}
                                    handleEnter={loginClicked}
                                />
                            </EditPanelSection>
                        )}

                        {isShowCompanySelect && !isShowOtpInput && (
                            <EditPanelSection>
                                <label htmlFor="company" className="field-label text-muted fs18 mb10">
                                    {translate('login.selectCompany')}
                                </label>
                                <Select
                                    name="company"
                                    options={companies}
                                    onChange={(newValue) => {
                                        const selectedCompanyId = newValue?.id ?? '';
                                        setCompany(selectedCompanyId);
                                        // Automatically submit when company is selected
                                        if (selectedCompanyId) {
                                            setTimeout(() => {
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                dispatch(login(emailAddress, password, otpToken, selectedCompanyId) as any);
                                            }, 0);
                                        }
                                    }}
                                    getOptionLabel={(item) => item.name}
                                    getOptionValue={(item) => item.id}
                                    styles={reactSelectDefaultStyles}
                                />
                            </EditPanelSection>
                        )}
                    </PanelBody>
                    <EditPanelButtons footer={true}>
                        <EditPanelButton primary={true} text={translate('login.signIn')} onClick={loginClicked} />
                    </EditPanelButtons>
                </form>
            </div>
            {isCustomerVersion('ronik') && (
                <div className="login-footer">
                    <p className="text-center">3D-Inspect is an application provided by ZXY Builders</p>
                </div>
            )}

            <div className="login-links mt25">
                <p>
                    <LinkButton className="active" onClick={props.navigateToForgetPassword}>
                        {translate('login.forgotPassword')}
                    </LinkButton>
                </p>
            </div>
        </>
    );
}
